import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────
function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toISOString().slice(0, 10);
  } catch {
    return s;
  }
}

function pct(n) {
  if (n == null) return "—";
  return `${Number(n).toFixed(1)}%`;
}

const VERDICT_STYLE = {
  BUILD: { bg: "var(--highlight)", color: "#000" },
  DELAY: { bg: "var(--surface-2)", color: "var(--text-muted)" },
  REJECT: { bg: "rgba(220,38,38,0.12)", color: "#fca5a5" },
};

const DECISION_STYLE = {
  approved: { bg: "rgba(16,185,129,0.15)", color: "#6ee7b7", label: "👍 Approved" },
  parked: { bg: "rgba(234,179,8,0.12)", color: "#fde68a", label: "🅿 Parked" },
  dismissed: { bg: "rgba(220,38,38,0.12)", color: "#fca5a5", label: "❌ Dismissed" },
};

// ────────────────────────────────────────────────────────────────────
// Small UI atoms
// ────────────────────────────────────────────────────────────────────
function Eyebrow({ children, right }) {
  return (
    <div className="flex items-baseline justify-between mb-3 flex-wrap gap-3">
      <p className="label-eyebrow">{children}</p>
      {right}
    </div>
  );
}

function CountRow({ label, value, sub }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 text-sm border-b border-white/5">
      <span className="text-neutral-300">{label}</span>
      <span className="text-neutral-500 text-xs flex items-baseline gap-3">
        {sub && <span>{sub}</span>}
        <span className="text-neutral-100">{value}</span>
      </span>
    </div>
  );
}

function EmptyHint({ children }) {
  return <p className="text-xs text-neutral-600 italic py-2">{children}</p>;
}

// ────────────────────────────────────────────────────────────────────
// Section components
// ────────────────────────────────────────────────────────────────────
function WhatCricketersWant({ data }) {
  if (!data) return null;
  return (
    <div className="bento-card p-5" data-testid="pm-want">
      <Eyebrow>1 · what cricketers want</Eyebrow>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
        <div>
          <p className="label-eyebrow mb-2 text-[10px]">requested topics</p>
          {(data.requested_topics || []).length === 0 ? <EmptyHint>nothing this week</EmptyHint> :
            data.requested_topics.map((r) => (
              <CountRow key={r.topic} label={r.topic} value={`${r.unique_users}u`} />
            ))}
        </div>
        <div>
          <p className="label-eyebrow mb-2 text-[10px]">technical topics</p>
          {(data.requested_technical_topics || []).length === 0 ? <EmptyHint>nothing this week</EmptyHint> :
            data.requested_technical_topics.map((r) => (
              <CountRow key={r.topic} label={r.topic} value={`${r.unique_users}u`} />
            ))}
        </div>
        <div className="md:col-span-2 mt-4">
          <p className="label-eyebrow mb-2 text-[10px]">requested video review themes</p>
          {(data.requested_video_themes || []).length === 0 ? <EmptyHint>—</EmptyHint> :
            data.requested_video_themes.map((r) => (
              <CountRow key={r.theme} label={r.theme} value={`${r.unique_users}u`} />
            ))}
        </div>
        <div className="md:col-span-2 mt-4">
          <p className="label-eyebrow mb-2 text-[10px]">feature requests (classified)</p>
          {(data.feature_requests || []).length === 0 ? <EmptyHint>no signal</EmptyHint> :
            data.feature_requests.map((r) => (
              <CountRow key={r.feature} label={r.feature.replace(/_/g, " ")} value={`${r.unique_users}u`} sub={`${r.count} events`} />
            ))}
        </div>
      </div>
    </div>
  );
}

function WhatCricketersStruggle({ data }) {
  if (!data) return null;
  return (
    <div className="bento-card p-5" data-testid="pm-struggle">
      <Eyebrow>2 · what cricketers struggle with</Eyebrow>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
        <div>
          <p className="label-eyebrow mb-2 text-[10px]">common challenges</p>
          {(data.challenges || []).length === 0 ? <EmptyHint>none above threshold</EmptyHint> :
            data.challenges.map((r) => (
              <CountRow key={r.challenge} label={r.challenge} value={`×${r.count}`} sub={`${r.unique_users}u`} />
            ))}
        </div>
        <div>
          <p className="label-eyebrow mb-2 text-[10px]">confidence themes</p>
          {(data.confidence_themes || []).length === 0 ? <EmptyHint>none above threshold</EmptyHint> :
            data.confidence_themes.map((r) => (
              <CountRow key={r.theme} label={r.theme} value={`×${r.count}`} sub={`${r.unique_users}u`} />
            ))}
        </div>
        <div className="mt-4">
          <p className="label-eyebrow mb-2 text-[10px]">confidence triggers</p>
          {(data.confidence_triggers || []).length === 0 ? <EmptyHint>—</EmptyHint> :
            data.confidence_triggers.map((r) => (
              <CountRow key={r.trigger} label={r.trigger} value={`${r.unique_users}u`} />
            ))}
        </div>
        <div className="mt-4">
          <p className="label-eyebrow mb-2 text-[10px]">technical issues (video)</p>
          {(data.technical_issues || []).length === 0 ? <EmptyHint>—</EmptyHint> :
            data.technical_issues.map((r) => (
              <CountRow key={r.issue} label={r.issue} value={`${r.unique_users}u`} />
            ))}
        </div>
      </div>
      <p className="text-[10px] text-neutral-600 mt-4">
        {data.low_confidence_user_count || 0} reports scored confidence ≤ 4.
      </p>
    </div>
  );
}

function WhatCricketersLove({ data }) {
  if (!data) return null;
  return (
    <div className="bento-card p-5" data-testid="pm-love">
      <Eyebrow>3 · what cricketers love</Eyebrow>
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div>
          <p className="label-eyebrow text-[10px] mb-1">helpful %</p>
          <p className="font-display text-3xl font-light">{pct(data.helpful_pct)}</p>
        </div>
        <div>
          <p className="label-eyebrow text-[10px] mb-1">NPS (normalised)</p>
          <p className="font-display text-3xl font-light"
             style={{ color: (data.nps_normalised || 0) >= 50 ? "var(--highlight)" : (data.nps_normalised || 0) < 0 ? "#fca5a5" : undefined }}>
            {data.nps_normalised != null ? Number(data.nps_normalised).toFixed(0) : "—"}
          </p>
        </div>
        <div>
          <p className="label-eyebrow text-[10px] mb-1">responses</p>
          <p className="font-display text-3xl font-light">{data.feedback_total || 0}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-6">
        <div>
          <p className="label-eyebrow text-[10px] mb-2">most helpful video components</p>
          {(data.helpful_review_components || []).length === 0 ? <EmptyHint>no feedback yet</EmptyHint> :
            data.helpful_review_components.map((c) => (
              <CountRow key={c.key} label={c.component} value={`×${c.count}`} sub={`${c.unique_users}u`} />
            ))}
        </div>
        <div>
          <p className="label-eyebrow text-[10px] mb-2">recurring strengths</p>
          {(data.top_strengths || []).length === 0 ? <EmptyHint>none above threshold</EmptyHint> :
            data.top_strengths.map((s) => (
              <CountRow key={s.strength} label={s.strength} value={`×${s.count}`} sub={`${s.unique_users}u`} />
            ))}
        </div>
      </div>
    </div>
  );
}

function RetentionDrivers({ data }) {
  if (!data) return null;
  const rf = data.report_funnel || {};
  const vf = data.video_funnel || {};
  const wr = data.weekly_return || {};
  const pr = data.predictors || {};
  return (
    <div className="bento-card p-5" data-testid="pm-retention">
      <Eyebrow>4 · what drives retention</Eyebrow>
      <div className="grid md:grid-cols-3 gap-x-6 gap-y-2 mb-3">
        <div>
          <p className="label-eyebrow text-[10px] mb-2">report funnel (lifetime)</p>
          <CountRow label="1+ report" value={rf.one_plus || 0} />
          <CountRow label="2+ reports" value={rf.two_plus || 0} sub={pct(rf.two_to_one_pct)} />
          <CountRow label="3+ reports" value={rf.three_plus || 0} sub={pct(rf.three_to_one_pct)} />
        </div>
        <div>
          <p className="label-eyebrow text-[10px] mb-2">video funnel (lifetime)</p>
          <CountRow label="1+ review" value={vf.one_plus || 0} />
          <CountRow label="2+ reviews" value={vf.two_plus || 0} sub={pct(vf.two_to_one_pct)} />
          <CountRow label="3+ reviews" value={vf.three_plus || 0} sub={pct(vf.three_to_one_pct)} />
        </div>
        <div>
          <p className="label-eyebrow text-[10px] mb-2">weekly return</p>
          <CountRow label="active last 7d" value={wr.active_last_7d || 0} />
          <CountRow label="active prev 7d" value={wr.active_prev_7d || 0} />
          <CountRow label="returned" value={wr.returning_users || 0} sub={pct(wr.return_rate_pct)} />
        </div>
      </div>
      <div className="border-t border-white/5 pt-3">
        <p className="label-eyebrow text-[10px] mb-2">behavioural predictors</p>
        <p className="text-xs text-neutral-400">
          Among <span className="text-neutral-100">{pr.three_plus_reporters || 0}</span> users with 3+ reports:
          <span className="text-neutral-100"> {pct(pr.three_plus_reporters_with_video_pct)}</span> tried a video review ·
          <span className="text-neutral-100"> {pct(pr.three_plus_reporters_who_gave_positive_feedback_pct)}</span> gave positive feedback.
        </p>
      </div>
    </div>
  );
}

function UnansweredQuestions({ data }) {
  if (!data) return null;
  return (
    <div className="bento-card p-5" data-testid="pm-unanswered">
      <Eyebrow>5 · questions gully couldn&apos;t answer
        <span className="ml-2 text-[10px] text-neutral-600">(gaps in understanding)</span>
      </Eyebrow>
      <div className="grid md:grid-cols-2 gap-x-6">
        <div>
          <p className="label-eyebrow text-[10px] mb-2">top gap clusters</p>
          {(data.top_gap_questions || []).length === 0 ? <EmptyHint>no recurring questions</EmptyHint> :
            (data.top_gap_questions || []).map((q, i) => (
              <div key={i} className="py-1.5 text-xs border-b border-white/5">
                <p className="text-neutral-200">{q.question}</p>
                <p className="text-neutral-600">×{q.count} · {q.unique_users} user{q.unique_users === 1 ? "" : "s"}</p>
              </div>
            ))}
        </div>
        <div>
          <p className="label-eyebrow text-[10px] mb-2">same user asked again</p>
          {(data.repeat_questions || []).length === 0 ? <EmptyHint>—</EmptyHint> :
            (data.repeat_questions || []).map((q, i) => (
              <div key={i} className="py-1.5 text-xs border-b border-white/5">
                <p className="text-neutral-300">{q.question}</p>
                <p className="text-neutral-600">{q.user_id?.slice(0, 14)}… ×{q.count}</p>
              </div>
            ))}
          <p className="label-eyebrow text-[10px] mt-4 mb-2">&quot;not helpful&quot; reasons</p>
          {(data.not_helpful_reasons || []).length === 0 ? <EmptyHint>no negative followups</EmptyHint> :
            (data.not_helpful_reasons || []).map((r) => (
              <CountRow key={r.reason} label={r.reason} value={`×${r.count}`} />
            ))}
        </div>
      </div>
    </div>
  );
}

function SignalCard({ s, decideFn, busyKey }) {
  const decision = s.decision;
  return (
    <div
      className="border border-white/5 rounded p-4 mb-3"
      data-testid={`pm-signal-${s.signal_key}`}
      style={{ background: decision === "dismissed" ? "rgba(0,0,0,0.3)" : "transparent" }}
    >
      <div className="flex items-baseline justify-between mb-1 gap-3">
        <span className="text-neutral-100 text-sm font-medium">{s.feature.replace(/_/g, " ")}</span>
        <span
          className="text-[10px] px-2 py-0.5 rounded uppercase tracking-widest"
          style={VERDICT_STYLE[s.recommendation] || {}}
        >
          {s.recommendation}
        </span>
      </div>
      <p className="text-xs text-neutral-500 mb-2">{s.description}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-neutral-500 mb-2">
        <span>demand: <span className="text-neutral-200">{s.user_demand?.unique_users || 0}u</span></span>
        <span>mission: <span className="text-neutral-200">{s.mission_alignment?.score ?? 0}/3</span></span>
        <span>retention: <span className="text-neutral-200">{s.retention_impact}</span></span>
        <span>complexity: <span className="text-neutral-200">{s.complexity}/10</span></span>
      </div>
      <p className="text-[11px] text-neutral-600 mb-3">{s.reason}</p>

      {decision ? (
        <span
          className="inline-block text-[10px] px-2 py-1 rounded"
          style={{ background: DECISION_STYLE[decision]?.bg, color: DECISION_STYLE[decision]?.color }}
          data-testid={`pm-decision-tag-${s.signal_key}`}
        >
          {DECISION_STYLE[decision]?.label} · {s.decision_note || "—"}
        </span>
      ) : (
        <div className="flex gap-2 flex-wrap">
          {["approved", "parked", "dismissed"].map((d) => (
            <button
              key={d}
              disabled={busyKey === s.signal_key}
              onClick={() => decideFn(s.signal_key, d)}
              data-testid={`pm-decide-${s.signal_key}-${d}`}
              className="text-[10px] px-2 py-1 rounded border border-white/10 hover:bg-white/10 disabled:opacity-40"
            >
              {DECISION_STYLE[d].label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FeatureSignals({ data, decideFn, busyKey }) {
  if (!data) return null;
  const signals = data.signals || [];
  const buildList = signals.filter((s) => s.recommendation === "BUILD");
  const delayList = signals.filter((s) => s.recommendation === "DELAY");
  const rejectList = signals.filter((s) => s.recommendation === "REJECT");

  return (
    <div className="bento-card p-5" data-testid="pm-signals">
      <Eyebrow>6 · feature signals
        <span className="ml-2 text-[10px] text-neutral-600">
          ({signals.length} total · BUILD {buildList.length} · DELAY {delayList.length} · REJECT {rejectList.length})
        </span>
      </Eyebrow>
      {signals.length === 0 ? <EmptyHint>no signals this window</EmptyHint> :
        signals.map((s) => <SignalCard key={s.signal_key} s={s} decideFn={decideFn} busyKey={busyKey} />)
      }
    </div>
  );
}

function FeaturesNotToBuild({ data }) {
  if (!data) return null;
  const guardrails = data.guardrail_rules || [];
  const rejected = (data.signals || []).filter((s) => s.guardrail_rejected);
  return (
    <div className="bento-card p-5" data-testid="pm-not-build">
      <Eyebrow>what features should NOT be built
        <span className="ml-2 text-[10px] text-neutral-600">(hard guardrails)</span>
      </Eyebrow>
      <ul className="text-xs space-y-1">
        {guardrails.map((g) => (
          <li key={g.slug} className="flex items-baseline gap-2 py-1 border-b border-white/5">
            <span className="text-[10px] uppercase tracking-widest text-neutral-600">{g.slug.replace(/_/g, " ")}</span>
            <span className="text-neutral-400">{g.reason}</span>
          </li>
        ))}
      </ul>
      {rejected.length > 0 && (
        <p className="text-[10px] text-neutral-600 mt-3">
          {rejected.length} signal{rejected.length === 1 ? "" : "s"} auto-rejected this window.
        </p>
      )}
    </div>
  );
}

function UserBands({ title, data, columns, testid, extraBands = [] }) {
  if (!data) return null;
  const baseBands = [["high_confidence", "high confidence"], ["emerging", "emerging"]];
  const bands = [...baseBands, ...extraBands];
  const totalsLine = baseBands
    .map(([k, label]) => `${label} ${data[`${k}_total`] || 0}`)
    .concat(extraBands.map(([k, label]) => `${label} ${data[`${k}_total`] || 0}`))
    .join(" · ");
  return (
    <div className="bento-card p-5" data-testid={testid}>
      <Eyebrow>{title}
        <span className="ml-2 text-[10px] text-neutral-600">
          ({totalsLine})
        </span>
      </Eyebrow>
      <div className={bands.length >= 3 ? "grid md:grid-cols-3 gap-x-6" : "grid md:grid-cols-2 gap-x-6"}>
        {bands.map(([k, label]) => (
          <div key={k}>
            <p className="label-eyebrow text-[10px] mb-2">{label}</p>
            {(data[k] || []).length === 0 ? <EmptyHint>no users this band</EmptyHint> :
              (data[k] || []).map((u, i) => (
                <div key={u.user_id || i} className="py-2 text-xs border-b border-white/5"
                     data-testid={`${testid}-${k}-row`}>
                  <p className="text-neutral-200">{u.name || u.email || u.user_id?.slice(0, 14)}</p>
                  <p className="text-neutral-600 mt-0.5">
                    {columns.map((c) => `${c.label}: ${u[c.key] ?? "—"}`).join(" · ")}
                  </p>
                  {u.reason && <p className="text-neutral-500 italic mt-1">{u.reason}</p>}
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function WelcomeFlow({ data }) {
  if (!data) return null;
  const has = data.welcome_messages_sent != null;
  return (
    <div className="bento-card p-5" data-testid="pm-welcome-flow">
      <Eyebrow>welcome flow · new user activation
        <span className="ml-2 text-[10px] text-neutral-600">
          ({data.since_days}-day window)
        </span>
      </Eyebrow>
      {!has || data.error ? (
        <EmptyHint>{data.error || "no welcome data yet"}</EmptyHint>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {[
              ["new users", data.new_users || 0],
              ["welcomes sent", data.welcome_messages_sent || 0],
              ["follow-ups sent", data.followups_sent || 0],
              ["users replied", data.users_replied || 0],
            ].map(([k, v]) => (
              <div key={k} className="surface-1 p-3" data-testid={`pm-welcome-${k.replace(/\s+/g, "-")}`}>
                <p className="label-eyebrow text-[10px] mb-1">{k}</p>
                <p className="font-display text-2xl font-light">{v}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-white/5 pt-3">
            <div>
              <p className="label-eyebrow text-[10px] mb-1">first-response rate</p>
              <p className="font-display text-2xl font-light"
                 style={{ color: (data.first_response_pct || 0) >= 60 ? "var(--highlight)" : undefined }}>
                {pct(data.first_response_pct)}
              </p>
            </div>
            <div>
              <p className="label-eyebrow text-[10px] mb-1">median TTFR</p>
              <p className="font-display text-2xl font-light">
                {data.ttfr_median_seconds == null ? "—" : `${Math.round(data.ttfr_median_seconds)}s`}
              </p>
            </div>
            <div>
              <p className="label-eyebrow text-[10px] mb-1">report conversion</p>
              <p className="font-display text-2xl font-light"
                 style={{ color: (data.report_conversion_pct || 0) >= 25 ? "var(--highlight)" : undefined }}>
                {pct(data.report_conversion_pct)}
              </p>
            </div>
          </div>
          <p className="text-[10px] text-neutral-600 mt-3">
            {data.continued_beyond_3_pct != null
              ? `${pct(data.continued_beyond_3_pct)} continued beyond 3 messages.`
              : ""}
          </p>
        </>
      )}
    </div>
  );
}

function CoachingQuality({ data }) {
  if (!data) return null;
  const has = data.total_replies != null && !data.error;
  const trend = data.trend_vs_prev_window_pct;
  return (
    <div className="bento-card p-5" data-testid="pm-coaching-quality">
      <Eyebrow>direct coaching quality
        <span className="ml-2 text-[10px] text-neutral-600">
          ({data.since_days}-day window · {data.total_replies || 0} replies)
        </span>
      </Eyebrow>
      {!has || data.error ? (
        <EmptyHint>{data.error || "no coaching quality data yet"}</EmptyHint>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <div className="surface-1 p-3" data-testid="pm-cq-action-plans">
              <p className="label-eyebrow text-[10px] mb-1">action plans</p>
              <p className="font-display text-2xl font-light">{data.action_plans_created || 0}</p>
            </div>
            <div className="surface-1 p-3" data-testid="pm-cq-avg-q-reply">
              <p className="label-eyebrow text-[10px] mb-1">avg q / reply</p>
              <p className="font-display text-2xl font-light">{data.avg_questions_per_reply ?? "—"}</p>
            </div>
            <div className="surface-1 p-3" data-testid="pm-cq-avg-q-conv">
              <p className="label-eyebrow text-[10px] mb-1">avg q / conversation</p>
              <p className="font-display text-2xl font-light">{data.avg_questions_per_conversation ?? "—"}</p>
            </div>
            <div className="surface-1 p-3" data-testid="pm-cq-direct">
              <p className="label-eyebrow text-[10px] mb-1">direct coaching %</p>
              <p className="font-display text-2xl font-light"
                 style={{ color: (data.direct_coaching_pct || 0) >= 50 ? "var(--highlight)" : undefined }}>
                {pct(data.direct_coaching_pct)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-white/5 pt-3">
            <div>
              <p className="label-eyebrow text-[10px] mb-1">quality flag rate</p>
              <p className="font-display text-2xl font-light"
                 style={{ color: (data.quality_flag_pct || 0) <= 10 ? "var(--highlight)" : (data.quality_flag_pct || 0) >= 25 ? "#fca5a5" : undefined }}>
                {pct(data.quality_flag_pct)}
              </p>
              {trend != null && (
                <p className={`text-[10px] mt-1 ${trend < 0 ? "text-emerald-400" : trend > 0 ? "text-red-400" : "text-neutral-600"}`}>
                  {trend < 0 ? "▼" : trend > 0 ? "▲" : "→"} {Math.abs(trend).toFixed(1)}pp vs prev window
                </p>
              )}
            </div>
            <div>
              <p className="label-eyebrow text-[10px] mb-1">question-only %</p>
              <p className="font-display text-2xl font-light">{pct(data.question_only_pct)}</p>
            </div>
            <div>
              <p className="label-eyebrow text-[10px] mb-1">multi-question %</p>
              <p className="font-display text-2xl font-light">{pct(data.multi_question_pct)}</p>
            </div>
          </div>
          <p className="text-[10px] text-neutral-600 mt-3">
            {data.reflection_word_violation_count
              ? `${data.reflection_word_violation_count} replies still used "reflection/journaling" language.`
              : `Language audit clean — no "reflection/journaling" leaks.`}
          </p>
          <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-white/5">
            <div data-testid="pm-cq-frustration">
              <p className="label-eyebrow text-[10px] mb-1">frustration signals (player msgs)</p>
              <p className="font-display text-2xl font-light"
                 style={{ color: (data.frustration_signals_count || 0) === 0 ? "var(--highlight)" : "#fca5a5" }}>
                {data.frustration_signals_count ?? 0}
              </p>
            </div>
            <div data-testid="pm-cq-inference">
              <p className="label-eyebrow text-[10px] mb-1">inference-as-fact violations</p>
              <p className="font-display text-2xl font-light"
                 style={{ color: (data.inference_as_fact_violations || 0) === 0 ? "var(--highlight)" : "#fca5a5" }}>
                {data.inference_as_fact_violations ?? 0}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TrendArrow({ value }) {
  if (value == null) return <span className="text-[10px] text-neutral-600">—</span>;
  const positive = value >= 0;
  const arrow = positive ? "▲" : "▼";
  // For metrics where UP is good we don't know the sense here; render neutral.
  const colour = value === 0 ? "text-neutral-500" : positive ? "text-emerald-400" : "text-red-400";
  return <span className={`text-[10px] ${colour}`}>{arrow} {Math.abs(value).toFixed(1)}%</span>;
}

function GrowthDashboard({ data }) {
  if (!data) return null;
  const cur = data.current_window || {};
  const trend = data.trend_pct_vs_prev || {};
  const tiles = [
    ["new users", cur.new_users, trend.new_users],
    ["welcomes sent", cur.welcome_messages_sent, trend.welcome_messages_sent],
    ["welcome reply rate", `${(cur.welcome_reply_rate_pct ?? 0).toFixed(1)}%`, trend.welcome_reply_rate_pct],
    ["1st report completion", `${(cur.first_report_completion_pct ?? 0).toFixed(1)}%`, trend.first_report_completion_pct],
    ["2nd conversation", cur.second_conversation_count, trend.second_conversation_count],
    ["reports completed", cur.reports_completed, trend.reports_completed],
    ["video uploads", cur.video_review_uploads, trend.video_review_uploads],
    ["weekly review eligible", data.weekly_review_eligible_users, null],
    ["DAU", cur.dau, null],
    ["WAU", cur.wau, null],
  ];
  return (
    <div className="bento-card p-5" data-testid="pm-growth-dashboard">
      <Eyebrow>growth dashboard
        <span className="ml-2 text-[10px] text-neutral-600">
          ({data.since_days}-day window · trends vs previous {data.since_days}-day window)
        </span>
      </Eyebrow>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {tiles.map(([k, v, t]) => (
          <div key={k} className="surface-1 p-3" data-testid={`pm-growth-${k.replace(/\s+/g, "-")}`}>
            <p className="label-eyebrow text-[10px] mb-1">{k}</p>
            <p className="font-display text-2xl font-light">{v ?? 0}</p>
            <div className="mt-1"><TrendArrow value={t} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RetentionFunnel({ data }) {
  if (!data) return null;
  const stages = data.stages || [];
  const top = stages.length ? stages[0].count : 0;
  return (
    <div className="bento-card p-5" data-testid="pm-retention-funnel">
      <Eyebrow>retention funnel
        <span className="ml-2 text-[10px] text-neutral-600">
          ({data.since_days}-day cohort)
        </span>
      </Eyebrow>
      {top === 0 ? <EmptyHint>{data.note || "no welcomed users in window yet"}</EmptyHint> : (
        <div className="space-y-2">
          {stages.map((s, i) => {
            const widthPct = top ? Math.max(2, Math.round((s.count / top) * 100)) : 0;
            return (
              <div key={s.stage} className="flex items-center gap-3" data-testid={`pm-funnel-stage-${i}`}>
                <div className="w-44 text-xs text-neutral-300">{s.stage}</div>
                <div className="flex-1 h-6 rounded bg-white/5 overflow-hidden">
                  <div className="h-full bg-[var(--highlight)]/30 flex items-center px-2 text-[11px] text-neutral-100"
                       style={{ width: `${widthPct}%` }}>
                    <span>{s.count}</span>
                  </div>
                </div>
                <div className="w-24 text-right text-[10px] text-neutral-500">
                  {i === 0 ? "—" : (
                    <>
                      <span className={s.drop_off_pct >= 50 ? "text-red-400" : s.drop_off_pct >= 25 ? "text-amber-400" : "text-emerald-400"}>
                        ↓ {s.drop_off_pct.toFixed(1)}%
                      </span>
                      <br />
                      <span>{s.vs_top_pct?.toFixed(1) ?? 0}% of top</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WeeklyReviewsReadiness({ data }) {
  if (!data) return null;
  const readyEnough = (data.eligible_users || 0) >= 10
    && (data.pct_with_patterns || 0) >= 60
    && (data.pct_with_confidence_trend || 0) >= 50;
  return (
    <div className="bento-card p-5" data-testid="pm-weekly-readiness">
      <Eyebrow>weekly reviews readiness
        <span className="ml-2 text-[10px] text-neutral-600">
          (current period {data.period_key} · AUTO_SEND {data.auto_send_enabled ? "ON" : "OFF"})
        </span>
      </Eyebrow>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="surface-1 p-3" data-testid="pm-wrr-eligible">
          <p className="label-eyebrow text-[10px] mb-1">eligible users</p>
          <p className="font-display text-2xl font-light">{data.eligible_users || 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-wrr-previews">
          <p className="label-eyebrow text-[10px] mb-1">previews stored</p>
          <p className="font-display text-2xl font-light">{data.preview_count || 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-wrr-avg-patterns">
          <p className="label-eyebrow text-[10px] mb-1">avg pattern count</p>
          <p className="font-display text-2xl font-light">{data.avg_pattern_count ?? 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-wrr-trend">
          <p className="label-eyebrow text-[10px] mb-1">% with conf. trend</p>
          <p className="font-display text-2xl font-light">{pct(data.pct_with_confidence_trend)}</p>
        </div>
      </div>
      <p className="text-[10px] mt-2"
         style={{ color: readyEnough ? "var(--highlight)" : "var(--text-muted)" }}>
        {readyEnough
          ? "✓ Heuristic ready: ≥10 eligible · ≥60% have patterns · ≥50% have confidence trend. Consider enabling AUTO_SEND."
          : "Hold — wait for ≥10 eligible users and ≥60% pattern coverage before flipping AUTO_SEND."}
      </p>
    </div>
  );
}

function _fmtNum(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n === "number" && !Number.isInteger(n)) return n.toFixed(2);
  return String(n);
}

function _deltaPill(delta, isRevenue = false) {
  if (delta === null || delta === undefined || delta === 0) {
    return <span className="text-[10px] text-neutral-600">flat</span>;
  }
  const positive = delta > 0;
  const sign = positive ? "+" : "";
  const prefix = isRevenue ? "$" : "";
  return (
    <span
      className="text-[10px]"
      style={{ color: positive ? "var(--highlight)" : "#f97316" }}
    >
      {sign}{prefix}{_fmtNum(delta)} vs yest
    </span>
  );
}

function DailyFounderSummaryCard({ data }) {
  if (!data) return null;
  const m = data.metrics || {};
  const today = new Date(data.today_start_utc || Date.now())
    .toISOString()
    .slice(0, 10);

  const tiles = [
    { key: "new_users", label: "new users", v: m.new_users, revenue: false },
    { key: "video_uploads", label: "video uploads", v: m.video_uploads, revenue: false },
    { key: "paywall_views", label: "paywall views", v: m.paywall_views, revenue: false },
    { key: "checkout_starts", label: "checkout starts", v: m.checkout_starts, revenue: false },
    { key: "successful_upgrades", label: "successful upgrades", v: m.successful_upgrades, revenue: false },
    { key: "revenue_usd", label: "revenue", v: m.revenue_usd, revenue: true },
    { key: "referral_conversions", label: "referral conversions", v: m.referral_conversions, revenue: false },
  ];

  return (
    <div
      className="bento-card p-5"
      data-testid="pm-daily-founder-summary"
      style={{ borderColor: "var(--highlight)" }}
    >
      <Eyebrow>
        daily founder summary{" "}
        <span className="ml-2 text-[10px] text-neutral-600">
          today (UTC {today}) · vs yesterday
        </span>
      </Eyebrow>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {tiles.map((t) => {
          const today = t.v?.today ?? 0;
          const delta = t.v?.delta ?? 0;
          const display = t.revenue ? `$${_fmtNum(today)}` : _fmtNum(today);
          return (
            <div
              key={t.key}
              className="surface-1 p-3"
              data-testid={`pm-dfs-${t.key.replace(/_/g, "-")}`}
            >
              <p className="label-eyebrow text-[10px] mb-1">{t.label}</p>
              <p className="font-display text-2xl font-light">{display}</p>
              <p className="mt-1">{_deltaPill(delta, t.revenue)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function PlusFunnelCard({ data }) {
  if (!data) return null;
  if (data.error) {
    return (
      <div className="bento-card p-5" data-testid="pm-plus-funnel">
        <Eyebrow>gully plus · conversion funnel</Eyebrow>
        <EmptyHint>{data.error}</EmptyHint>
      </div>
    );
  }
  return (
    <div
      className="bento-card p-5"
      data-testid="pm-plus-funnel"
      style={{ borderColor: "var(--highlight)" }}
    >
      <Eyebrow>
        gully plus · conversion funnel{" "}
        <span className="ml-2 text-[10px] text-neutral-600">
          ${data.price_usd ?? 9.99}/mo · ${data.revenue_usd ?? 0} captured
        </span>
      </Eyebrow>

      {/* Plus-page funnel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3" data-testid="pm-plus-funnel-pluspage">
        <div className="surface-1 p-3" data-testid="pm-plus-page-views">
          <p className="label-eyebrow text-[10px] mb-1">plus page views</p>
          <p className="font-display text-2xl font-light">{data.plus_page_views || 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-plus-checkout-starts">
          <p className="label-eyebrow text-[10px] mb-1">checkout starts</p>
          <p className="font-display text-2xl font-light">{data.checkout_starts || 0}</p>
          <p className="text-[10px] text-neutral-600 mt-1">
            {data.checkout_starts_from_plus_page || 0} page · {data.checkout_starts_from_paywall || 0} paywall
          </p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-plus-upgrades">
          <p className="label-eyebrow text-[10px] mb-1">successful upgrades</p>
          <p className="font-display text-2xl font-light">{data.successful_upgrades || 0}</p>
          <p className="text-[10px] text-neutral-600 mt-1">
            {data.upgrades_from_plus_page || 0} page · {data.upgrades_from_paywall || 0} paywall
          </p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-plus-conversion">
          <p className="label-eyebrow text-[10px] mb-1">page conversion</p>
          <p className="font-display text-2xl font-light">{pct(data.plus_page_conversion_pct)}</p>
          <p className="text-[10px] text-neutral-600 mt-1">
            checkout→upgrade {pct(data.checkout_to_upgrade_pct)}
          </p>
        </div>
      </div>

      {/* Paywall-only sub-funnel */}
      <div
        className="surface-1 p-3 mb-1"
        style={{ borderLeft: "3px solid var(--highlight)" }}
        data-testid="pm-paywall-funnel"
      >
        <p className="label-eyebrow text-[10px] mb-2" style={{ color: "var(--highlight)" }}>
          paywall sub-funnel
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div data-testid="pm-paywall-views">
            <p className="label-eyebrow text-[10px] mb-1">paywall views</p>
            <p className="font-display text-xl">{data.paywall_views || 0}</p>
          </div>
          <div data-testid="pm-paywall-checkouts">
            <p className="label-eyebrow text-[10px] mb-1">checkout from paywall</p>
            <p className="font-display text-xl">{data.checkout_starts_from_paywall || 0}</p>
          </div>
          <div data-testid="pm-paywall-upgrades">
            <p className="label-eyebrow text-[10px] mb-1">upgrades from paywall</p>
            <p className="font-display text-xl">{data.upgrades_from_paywall || 0}</p>
          </div>
          <div data-testid="pm-paywall-conversion">
            <p className="label-eyebrow text-[10px] mb-1">paywall conversion</p>
            <p className="font-display text-xl">{pct(data.paywall_conversion_pct)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}


function EngagedReengagementCard({ data }) {
  if (!data) return null;
  if (data.error) {
    return (
      <div className="bento-card p-5" data-testid="pm-engaged-reengagement">
        <Eyebrow>engaged video re-engagement</Eyebrow>
        <EmptyHint>{data.error}</EmptyHint>
      </div>
    );
  }
  const byCohort = data.by_cohort || {};
  return (
    <div className="bento-card p-5" data-testid="pm-engaged-reengagement">
      <Eyebrow>
        engaged video re-engagement{" "}
        <span className="ml-2 text-[10px] text-neutral-600">
          (3+ / 4+ / 7+ video cohorts · highest-tier only · once per user)
        </span>
      </Eyebrow>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <div className="surface-1 p-3" data-testid="pm-eng-sent">
          <p className="label-eyebrow text-[10px] mb-1">messages sent</p>
          <p className="font-display text-2xl font-light">{data.messages_sent || 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-eng-replies">
          <p className="label-eyebrow text-[10px] mb-1">replies</p>
          <p className="font-display text-2xl font-light">{data.replies || 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-eng-reply-rate">
          <p className="label-eyebrow text-[10px] mb-1">reply rate</p>
          <p className="font-display text-2xl font-light">{pct(data.reply_rate_pct)}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-eng-more-videos">
          <p className="label-eyebrow text-[10px] mb-1">more videos after</p>
          <p className="font-display text-2xl font-light">{data.additional_videos_after_nudge || 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-eng-plus-upgrades">
          <p className="label-eyebrow text-[10px] mb-1">plus upgrades</p>
          <p className="font-display text-2xl font-light">{data.plus_upgrades_from_campaign || 0}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {["3_plus", "4_plus", "7_plus"].map((k) => {
          const c = byCohort[k] || {};
          return (
            <div key={k} className="surface-1 p-3" data-testid={`pm-eng-cohort-${k}`}>
              <p className="label-eyebrow text-[10px] mb-2">{k.replace("_plus", "+")} reviews cohort</p>
              <CountRow
                label="messages sent"
                value={c.messages_sent_all_time ?? 0}
                sub={`${c.messages_sent_window ?? 0} in window`}
              />
              <CountRow label="replies" value={c.replies ?? 0} sub={`${pct(c.reply_rate_pct)}`} />
              <CountRow
                label="continued"
                value={c.continued_conversations ?? 0}
                sub={`${pct(c.continued_conversation_pct)}`}
              />
              <CountRow label="more videos" value={c.additional_videos_after_nudge ?? 0} />
              <CountRow label="plus upgrades" value={c.plus_upgrades_from_cohort ?? 0} />
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ReferralNudgeCard({ data }) {
  if (!data) return null;
  if (data.error) {
    return (
      <div className="bento-card p-5" data-testid="pm-referral-nudge">
        <Eyebrow>auto referral nudge · post-video</Eyebrow>
        <EmptyHint>{data.error}</EmptyHint>
      </div>
    );
  }
  return (
    <div className="bento-card p-5" data-testid="pm-referral-nudge">
      <Eyebrow>
        auto referral nudge · post-video{" "}
        <span className="ml-2 text-[10px] text-neutral-600">
          (sent after {data.min_videos_required ?? 2}nd+ video · {data.throttle_days ?? 7}-day throttle)
        </span>
      </Eyebrow>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="surface-1 p-3" data-testid="pm-refnudge-sent">
          <p className="label-eyebrow text-[10px] mb-1">messages sent</p>
          <p className="font-display text-2xl font-light">{data.messages_sent_all_time || 0}</p>
          <p className="text-[10px] text-neutral-600 mt-1">{data.messages_sent_window || 0} in window</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-refnudge-shares">
          <p className="label-eyebrow text-[10px] mb-1">shares (proxy)</p>
          <p className="font-display text-2xl font-light">{data.shares_all_time || 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-refnudge-conversions">
          <p className="label-eyebrow text-[10px] mb-1">referrals from nudged</p>
          <p className="font-display text-2xl font-light">{data.referrals_from_nudged_users_all_time || 0}</p>
          <p className="text-[10px] text-neutral-600 mt-1">
            {data.referrals_from_nudged_users_window || 0} in window
          </p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-refnudge-conversion-pct">
          <p className="label-eyebrow text-[10px] mb-1">conversion rate</p>
          <p className="font-display text-2xl font-light">{pct(data.conversion_rate_pct)}</p>
        </div>
      </div>
    </div>
  );
}


function ReferralEngineCard({ data, onActioned }) {
  if (!data) return null;
  if (data.error) {
    return (
      <div className="bento-card p-5" data-testid="pm-referral-engine">
        <Eyebrow>referral engine · ambassador rewards</Eyebrow>
        <EmptyHint>{data.error}</EmptyHint>
      </div>
    );
  }
  const tier1 = data.reward_tiers?.tier_1_bonus_video ?? 1;
  const tier3 = data.reward_tiers?.tier_3_free_plus ?? 3;
  const tier10 = data.reward_tiers?.tier_10_founder_review ?? 10;
  return (
    <div className="bento-card p-5" data-testid="pm-referral-engine"
         style={{ borderColor: "var(--highlight)" }}>
      <Eyebrow>referral engine · ambassador rewards
        <span className="ml-2 text-[10px] text-neutral-600">
          (tiers: {tier1} → +1 video · {tier3} → 1 mo Plus · {tier10} → founder review)
        </span>
      </Eyebrow>

      {/* Top-line metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <div className="surface-1 p-3" data-testid="pm-ref-sent">
          <p className="label-eyebrow text-[10px] mb-1">referrals sent</p>
          <p className="font-display text-2xl font-light">{data.referrals_sent_all_time || 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-ref-conv">
          <p className="label-eyebrow text-[10px] mb-1">conversion</p>
          <p className="font-display text-2xl font-light">{pct(data.conversion_rate_pct)}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-ref-activation">
          <p className="label-eyebrow text-[10px] mb-1">ambassador activation</p>
          <p className="font-display text-2xl font-light">{pct(data.activation_rate_pct)}</p>
          <p className="text-[10px] text-neutral-600 mt-1">{data.active_ambassadors}/{data.total_ambassadors}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-ref-videos-earned">
          <p className="label-eyebrow text-[10px] mb-1">+1 video awards</p>
          <p className="font-display text-2xl font-light">{data.reviews_earned_through_referrals || 0}</p>
        </div>
        <div className="surface-1 p-3" data-testid="pm-ref-plus-upgrades">
          <p className="label-eyebrow text-[10px] mb-1">plus upgrades (1 mo)</p>
          <p className="font-display text-2xl font-light">{data.plus_upgrades_from_referrals || 0}</p>
        </div>
      </div>

      {/* Founder Review Eligible — high-signal action area */}
      <div className="surface-1 p-4 mb-4" style={{ borderLeft: "3px solid var(--highlight)" }}
           data-testid="pm-ref-founder-review">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="label-eyebrow text-[11px]" style={{ color: "var(--highlight)" }}>
            🏏 founder review eligible · {data.founder_review_eligible_count || 0} cricketer{data.founder_review_eligible_count === 1 ? "" : "s"}
          </p>
          <span className="text-[10px] text-neutral-600">
            10+ referrals · no auto-reward · you decide
          </span>
        </div>
        {(data.founder_review_eligible_users || []).length === 0 ? (
          <EmptyHint>No cricketer has hit 10 referrals yet.</EmptyHint>
        ) : (
          <div className="space-y-1">
            {data.founder_review_eligible_users.map((u) => (
              <div key={u.code}
                   className="flex items-center justify-between text-xs gap-2 py-1.5 border-b border-white/5"
                   data-testid={`pm-ref-fr-${u.code}`}>
                <div className="flex items-baseline gap-2 flex-1 min-w-0">
                  <span className="font-mono text-[#A7F3D0]">{u.code}</span>
                  <span className="text-neutral-300 truncate">{u.name || u.email || "—"}</span>
                  <span className="text-neutral-600">· {u.verified_joins} refs</span>
                  {u.actioned && (
                    <span className="text-[10px] text-emerald-400 ml-1">✓ actioned</span>
                  )}
                </div>
                {!u.actioned && (
                  <button
                    onClick={() => onActioned && onActioned(u.code)}
                    className="px-2 py-1 rounded text-[10px] bg-white/10 text-neutral-100 hover:bg-white/20"
                    data-testid={`pm-ref-fr-action-${u.code}`}
                  >
                    mark actioned
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top referrers — this week vs all time */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <p className="label-eyebrow text-[10px] mb-2">top referrers · this week</p>
          {(data.top_referrers_week || []).length === 0 ? <EmptyHint>no referrals this week</EmptyHint> :
            (data.top_referrers_week || []).map((r, i) => (
              <CountRow
                key={r.code}
                label={`#${i + 1} ${r.code}${r.name ? ` · ${r.name}` : ""}`}
                value={`${r.verified_joins_week} this week`}
                sub={`${r.verified_joins_total} total`}
              />
            ))}
        </div>
        <div>
          <p className="label-eyebrow text-[10px] mb-2">top referrers · all time</p>
          {(data.top_referrers_all_time || []).length === 0 ? <EmptyHint>no verified referrals yet</EmptyHint> :
            (data.top_referrers_all_time || []).map((r, i) => (
              <CountRow
                key={r.code}
                label={`#${i + 1} ${r.code}${r.name ? ` · ${r.name}` : ""}${r.founder_review_eligible ? " 🏏" : ""}`}
                value={`${r.verified_joins}`}
              />
            ))}
        </div>
      </div>
    </div>
  );
}


function ReengagementCard({ data }) {
  if (!data) return null;
  const has = data.messages_sent != null && !data.error;
  return (
    <div className="bento-card p-5" data-testid="pm-reengagement">
      <Eyebrow>re-engagement campaign
        <span className="ml-2 text-[10px] text-neutral-600">
          ({data.since_days}-day window · {data.eligible_dormant_now ?? 0} dormant users still eligible)
        </span>
      </Eyebrow>
      {!has || data.error ? (
        <EmptyHint>{data.error || "no campaign data yet"}</EmptyHint>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <div className="surface-1 p-3" data-testid="pm-re-sent">
              <p className="label-eyebrow text-[10px] mb-1">messages sent</p>
              <p className="font-display text-2xl font-light">{data.messages_sent || 0}</p>
            </div>
            <div className="surface-1 p-3" data-testid="pm-re-replied">
              <p className="label-eyebrow text-[10px] mb-1">replies</p>
              <p className="font-display text-2xl font-light">{data.replies_received || 0}</p>
            </div>
            <div className="surface-1 p-3" data-testid="pm-re-restarted">
              <p className="label-eyebrow text-[10px] mb-1">conversations restarted</p>
              <p className="font-display text-2xl font-light">{data.conversations_restarted || 0}</p>
            </div>
            <div className="surface-1 p-3" data-testid="pm-re-continued">
              <p className="label-eyebrow text-[10px] mb-1">continued &gt; 3 msg</p>
              <p className="font-display text-2xl font-light">{data.continued_beyond_3 || 0}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-white/5 pt-3">
            <div>
              <p className="label-eyebrow text-[10px] mb-1">reply rate</p>
              <p className="font-display text-2xl font-light"
                 style={{ color: (data.reply_rate_pct || 0) >= 15 ? "var(--highlight)" : undefined }}>
                {pct(data.reply_rate_pct)}
              </p>
            </div>
            <div>
              <p className="label-eyebrow text-[10px] mb-1">median TTFR</p>
              <p className="font-display text-2xl font-light">
                {data.ttfr_median_seconds == null ? "—" :
                  data.ttfr_median_seconds > 3600
                    ? `${(data.ttfr_median_seconds / 3600).toFixed(1)}h`
                    : `${Math.round(data.ttfr_median_seconds / 60)}m`}
              </p>
            </div>
            <div>
              <p className="label-eyebrow text-[10px] mb-1">restarted %</p>
              <p className="font-display text-2xl font-light">{pct(data.conversations_restarted_pct)}</p>
            </div>
          </div>
          {(data.variants || []).length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/5">
              <p className="label-eyebrow text-[10px] mb-2">variants (A/B-ready)</p>
              <div className="space-y-1">
                {data.variants.map((v) => (
                  <div key={v.variant} className="flex justify-between text-xs text-neutral-400"
                       data-testid={`pm-re-variant-${v.variant}`}>
                    <span className="text-neutral-200">{v.variant}</span>
                    <span>sent {v.sent} · replied {v.replied} · {pct(v.reply_rate_pct)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PlusSignals({ data }) {
  if (!data) return null;
  const ranked = data.ranked_prospects || [];
  return (
    <div className="bento-card p-5" data-testid="pm-plus">
      <Eyebrow>9 · plus signals
        <span className="ml-2 text-[10px] text-neutral-600">
          (free cap = {data.free_video_limit} videos)
        </span>
      </Eyebrow>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {[
          ["at cap", data.at_cap_count || 0],
          ["near cap", data.near_cap_count || 0],
          ["pricing intent", data.pricing_intent_count || 0],
          ["repeat uploaders", data.repeat_uploader_count || 0],
        ].map(([k, v]) => (
          <div key={k} className="surface-1 p-3">
            <p className="label-eyebrow text-[10px] mb-1">{k}</p>
            <p className="font-display text-2xl font-light">{v}</p>
          </div>
        ))}
      </div>
      <p className="label-eyebrow text-[10px] mb-2">ranked prospects (plus_score)</p>
      {ranked.length === 0 ? <EmptyHint>no prospects yet</EmptyHint> :
        <div className="overflow-x-auto -mx-2">
          <table className="text-xs w-full">
            <thead className="text-neutral-600">
              <tr className="border-b border-white/5">
                <th className="text-left py-1.5 px-2">player</th>
                <th className="text-right">videos</th>
                <th className="text-right">priced?</th>
                <th className="text-right">score</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((r) => (
                <tr key={r.user_id} className="border-b border-white/5">
                  <td className="py-1.5 px-2 text-neutral-200">{r.name || r.email || r.user_id?.slice(0, 14)}</td>
                  <td className="text-right text-neutral-300">{r.videos}</td>
                  <td className="text-right">{r.showed_pricing_interest ? "✓" : "—"}</td>
                  <td className="text-right text-neutral-100">{r.plus_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Main tab
// ────────────────────────────────────────────────────────────────────
export default function GullyPMTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/pm?since_days=7");
        if (!cancelled) setReport(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load Gully PM");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const trigger = async () => {
    setRunning(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/pm/run?since_days=7");
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const decide = async (signal_key, decision) => {
    setBusyKey(signal_key);
    try {
      await api.post("/admin/pm/decisions", { signal_key, decision });
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Decision failed");
    } finally {
      setBusyKey(null);
    }
  };

  const markFounderReviewActioned = async (code) => {
    try {
      await api.post("/admin/ambassadors/founder-review/actioned", { code });
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't mark actioned");
    }
  };

  const generatedAt = useMemo(() => fmtDate(report?.generated_at), [report]);

  return (
    <div className="mb-12" data-testid="gully-pm-section">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="label-eyebrow">gully pm · founder intelligence</p>
          <p className="text-[11px] text-neutral-600 mt-1">
            deterministic v1 · no LLM synthesis · window = {report?.since_days || 7} days · generated {generatedAt}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button
            data-testid="pm-run"
            onClick={trigger}
            disabled={running}
            className="px-3 py-1 rounded bg-white/10 text-neutral-100 hover:bg-white/20 disabled:opacity-50"
          >
            {running ? "running…" : "regenerate"}
          </button>
          <button
            data-testid="pm-refresh"
            onClick={() => setRefreshTick((t) => t + 1)}
            className="px-3 py-1 rounded text-neutral-500 hover:text-neutral-300"
          >
            ↻
          </button>
        </div>
      </div>

      {loading && <p className="text-neutral-600 text-xs py-4" data-testid="pm-loading">Loading…</p>}
      {error && <p className="text-neutral-500 text-sm italic py-4" data-testid="pm-error">{error}</p>}

      {!loading && !error && report && (
        <div className="space-y-5">
          <DailyFounderSummaryCard data={report.daily_founder_summary} />
          <GrowthDashboard data={report.growth_dashboard} />
          <RetentionFunnel data={report.retention_funnel} />
          <WhatCricketersWant data={report.what_cricketers_want} />
          <WhatCricketersStruggle data={report.what_cricketers_struggle} />
          <WhatCricketersLove data={report.what_cricketers_love} />
          <RetentionDrivers data={report.retention_drivers} />
          <UnansweredQuestions data={report.unanswered_questions} />
          <FeatureSignals data={report.feature_signals} decideFn={decide} busyKey={busyKey} />
          <UserBands
            title="7 · churn risk"
            data={report.churn_risk}
            testid="pm-churn"
            columns={[
              { key: "days_idle", label: "idle" },
              { key: "reports", label: "reports" },
              { key: "videos", label: "videos" },
              { key: "confidence_trend", label: "conf" },
              { key: "engagement_score", label: "score" },
            ]}
            extraBands={[["sprint_flag", "stabilization flag (≥1 report · idle ≥7d)"]]}
          />
          <UserBands
            title="8 · power users"
            data={report.power_users}
            testid="pm-power"
            columns={[
              { key: "reports", label: "reports" },
              { key: "videos", label: "videos" },
              { key: "engagement_score", label: "score" },
              { key: "ambassador_code", label: "amb" },
            ]}
            extraBands={[["top_engaged", "top 20 by engagement"]]}
          />
          <WelcomeFlow data={report.welcome_flow} />
          <CoachingQuality data={report.coaching_quality} />
          <PlusFunnelCard data={report.plus_funnel} />
          <ReferralEngineCard data={report.referral_engine} onActioned={markFounderReviewActioned} />
          <ReferralNudgeCard data={report.referral_nudge} />
          <EngagedReengagementCard data={report.engaged_reengagement} />
          <ReengagementCard data={report.reengagement} />
          <WeeklyReviewsReadiness data={report.weekly_reviews_readiness} />
          <PlusSignals data={report.plus_signals} />
          <FeaturesNotToBuild data={report.feature_signals} />
        </div>
      )}
    </div>
  );
}
